import { useEffect, useState } from "react";
import { ApiError, api } from "@/lib/api";
import CorreosParaIA from "@/components/correo/CorreosParaIA";
import { ayudaTicketQueclink } from "@/components/ayuda/vistas/ticketQueclink";
import AyudaVista from "@/ui/AyudaVista";
import Modal from "@/ui/Modal";
import { useToast } from "@/ui/Toast";

type NotaBitacora = {
  fecha: string;
  autor: string | null;
  texto: string;
  tags: string[];
};

type Prefill = {
  asunto: string;
  cuerpo: string;
  bitacora: NotaBitacora[];
  evidencia_url: string | null;
  puede_generar_evidencia: boolean;
  ia_habilitada: boolean;
};

type Props = {
  casoId: number;
  onClose: () => void;
  onCreado: () => void;
  /** «crear» (default) o «manual» = «Ya tengo el número»: ligar un ticket que ya existe. */
  modoInicial?: "crear" | "manual";
};

/**
 * Abre un ticket en el portal de Queclink desde el caso.
 *
 * El asunto viene pre-armado como `MODELO - IMEI - [TE]`. Ese formato no es
 * capricho: el `[TE]` es lo que después permite que ArkTickets reconozca el
 * ticket y lo ligue solo al caso.
 *
 * Trae a la mano lo que hace falta para redactar sin salir del modal —los
 * apuntes de la bitácora y el link del ZIP de evidencias— porque si no hay que
 * cerrarlo e irse a otra pestaña a buscar de qué se trata el caso.
 *
 * El envío pide confirmación: un ticket abierto ante el proveedor **no se
 * puede borrar** desde nuestra cuenta.
 *
 * Modo «Ya tengo el número» (mismo patrón que el modal de Jimi): liga un ticket
 * que YA existe en Queclink pegando su número. Es la salida para los tickets
 * cuyo asunto no trae el TE (los abiertos a mano o desde el correo), que
 * «Buscar ticket» no encuentra. El backend comprueba contra Zendesk que exista.
 */
export default function CrearTicketQueclinkModal({
  casoId,
  onClose,
  onCreado,
  modoInicial = "crear",
}: Props) {
  const toast = useToast();
  const [modo, setModo] = useState<"crear" | "manual">(modoInicial);
  const [numero, setNumero] = useState("");
  const [ligando, setLigando] = useState(false);
  const [pre, setPre] = useState<Prefill | null>(null);
  const [asunto, setAsunto] = useState("");
  const [cuerpo, setCuerpo] = useState("");
  const [evidenciaUrl, setEvidenciaUrl] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);
  const [creando, setCreando] = useState(false);
  const [generando, setGenerando] = useState(false);
  const [redactando, setRedactando] = useState(false);
  const [verApuntes, setVerApuntes] = useState(false);
  // Correos del caso que la IA puede leer. En cero por decisión de Joe:
  // él prende los que sirven (ver `components/correo/CorreosParaIA`).
  const [correosIA, setCorreosIA] = useState<number[]>([]);
  const [confirmando, setConfirmando] = useState(false);

  useEffect(() => {
    let vivo = true;
    void (async () => {
      try {
        const r = await api.get<Prefill>(`/api/casos/${casoId}/queclink/prefill`);
        if (!vivo) return;
        setPre(r);
        setAsunto(r.asunto);
        setCuerpo(r.cuerpo);
        setEvidenciaUrl(r.evidencia_url);
        setVerApuntes(r.bitacora.length > 0);
      } catch (err) {
        const msg = err instanceof ApiError ? err.detail : "Error";
        toast.error("No pude preparar el ticket", msg);
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => {
      vivo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [casoId]);

  const incompleto = !asunto.trim() || !cuerpo.trim();

  function agregarAlFinal(texto: string) {
    setCuerpo((prev) => (prev.trim() ? `${prev.trim()}\n\n${texto}` : texto));
    setConfirmando(false);
  }

  async function generarEvidencia() {
    if (generando) return;
    setGenerando(true);
    try {
      // Mismo endpoint que usa el modal de Jimi: arma un ZIP SIN contraseña
      // (así el proveedor no batalla) y devuelve el link firmado.
      const r = await api.post<{ evidencia_url: string }>(
        `/api/casos/${casoId}/jimi/evidencia`,
      );
      setEvidenciaUrl(r.evidencia_url);
      toast.success("Paquete de evidencias listo", "Ya puedes agregarlo al ticket");
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude generar el paquete", msg);
    } finally {
      setGenerando(false);
    }
  }

  async function redactarConIA() {
    if (redactando) return;
    setRedactando(true);
    try {
      const r = await api.post<{ texto: string }>(
        `/api/casos/${casoId}/correos/generar-comentario`,
        { destinatario: "jimi", guia: cuerpo, correos_ids: correosIA },
      );
      setCuerpo(r.texto);
      setConfirmando(false);
      toast.success("Listo, ya lo redactó la IA", "Revísalo antes de abrir el ticket");
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude redactarlo", msg);
    } finally {
      setRedactando(false);
    }
  }

  async function crear() {
    if (incompleto || creando) return;
    setCreando(true);
    try {
      const r = await api.post<{ ticket_id: number }>(
        `/api/casos/${casoId}/queclink/crear`,
        { asunto, cuerpo },
      );
      toast.success("Ticket abierto en Queclink", `#${r.ticket_id}`);
      onCreado();
      onClose();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude abrir el ticket", msg);
      setConfirmando(false);
    } finally {
      setCreando(false);
    }
  }

  async function ligar() {
    if (ligando || !numero.trim()) return;
    setLigando(true);
    try {
      const r = await api.post<{ ticket_id: number | null }>(
        `/api/casos/${casoId}/queclink/vincular-manual`,
        { ticket: numero },
      );
      toast.success("Ticket de Queclink ligado", `#${r.ticket_id}`);
      onCreado();
      onClose();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude ligar el ticket", msg);
    } finally {
      setLigando(false);
    }
  }

  const ocupado = creando || ligando;
  const titulo = modo === "manual" ? "Ligar ticket de Queclink" : "Abrir ticket en Queclink";

  return (
    <Modal
      open
      onOpenChange={(o) => {
        if (!o && !ocupado) onClose();
      }}
      title={titulo}
      ayuda={
        <AyudaVista
          titulo={titulo}
          {...ayudaTicketQueclink({ modo, iaHabilitada: Boolean(pre?.ia_habilitada) })}
        />
      }
      description={
        modo === "manual"
          ? "Un ticket que ya existe en el portal de Queclink"
          : "Se crea en el portal de soporte de Queclink"
      }
      size={modo === "manual" ? "md" : "lg"}
      footer={
        <>
          <button
            type="button"
            className="btn-ghost text-sm"
            onClick={onClose}
            disabled={ocupado}
          >
            Cancelar
          </button>
          {modo === "manual" ? (
            <button
              type="button"
              className="btn-primary text-sm"
              onClick={() => void ligar()}
              disabled={ligando || !numero.trim()}
            >
              {ligando ? "Comprobando…" : "Ligar ticket"}
            </button>
          ) : confirmando ? (
            <button
              type="button"
              className="btn bg-amber-600 hover:bg-amber-500 text-white text-sm"
              onClick={() => void crear()}
              disabled={creando}
            >
              {creando ? "Abriendo…" : "Sí, abrir el ticket"}
            </button>
          ) : (
            <button
              type="button"
              className="btn-primary text-sm"
              onClick={() => setConfirmando(true)}
              disabled={incompleto || cargando}
            >
              Abrir ticket
            </button>
          )}
        </>
      }
    >
      {/* Segmented control: crear / ya tengo el número (mismo control que Jimi) */}
      <div className="mb-3 inline-flex rounded-lg border border-ink-700 bg-ink-800 p-0.5 text-sm">
        {(["crear", "manual"] as const).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setModo(m)}
            disabled={ocupado}
            className={`rounded-md px-3 py-1.5 ${
              modo === m ? "bg-ink-700 text-zinc-100" : "text-zinc-400 hover:text-zinc-200"
            }`}
          >
            {m === "crear" ? "Crear nuevo" : "Ya tengo el número"}
          </button>
        ))}
      </div>

      {modo === "manual" ? (
        <div className="max-w-md space-y-2">
          <label className="text-sm">
            <span className="mb-1 block text-zinc-400">
              Número de ticket de Queclink <span className="text-red-400">*</span>
            </span>
            <input
              className="input w-full font-mono"
              value={numero}
              onChange={(e) => setNumero(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void ligar();
              }}
              placeholder="38726"
              autoFocus
            />
          </label>
          <p className="text-[11px] text-zinc-500">
            Es el número que aparece en el asunto de sus correos («Su solicitud de soporte
            (38726)») y en su portal. Se comprueba que exista antes de ligarlo; en la
            siguiente revisión su hilo entra a la bitácora.
          </p>
        </div>
      ) : cargando ? (
        <p className="text-sm text-zinc-500">Preparando…</p>
      ) : (
        <div className="space-y-3">
          <p className="text-xs text-amber-300/90 bg-amber-500/10 border border-amber-500/30 rounded-md px-3 py-2">
            Esto abre un ticket <strong>en el sistema de Queclink</strong> y{" "}
            <strong>no se puede borrar</strong> después. Revísalo bien.
          </p>

          <label className="block text-xs text-zinc-400">
            Asunto
            <input
              value={asunto}
              onChange={(e) => {
                setAsunto(e.target.value);
                setConfirmando(false);
              }}
              disabled={creando}
              className="input w-full mt-1"
            />
          </label>
          <p className="text-[11px] text-zinc-500 -mt-2">
            Deja el <strong>[TE-…]</strong> en el asunto: es lo que hace que el
            ticket se ligue solo a este caso.
          </p>

          <label className="block text-xs text-zinc-400">
            Descripción
            <textarea
              value={cuerpo}
              onChange={(e) => {
                setCuerpo(e.target.value);
                setConfirmando(false);
              }}
              rows={8}
              disabled={creando}
              className="input w-full mt-1 font-normal"
              placeholder="Describe la falla para el equipo de Queclink…"
            />
          </label>

          <div className="flex flex-wrap items-center gap-2">
            {pre?.ia_habilitada && (
              <button
                type="button"
                onClick={() => void redactarConIA()}
                disabled={redactando || creando}
                className="btn-ghost text-xs disabled:opacity-50"
              >
                {redactando ? "Redactando…" : "✨ Redactar con IA"}
              </button>
            )}

            {evidenciaUrl ? (
              <button
                type="button"
                onClick={() =>
                  agregarAlFinal(`Evidencias del equipo: ${evidenciaUrl}`)
                }
                disabled={creando}
                className="btn-ghost text-xs disabled:opacity-50"
              >
                📎 Agregar link de evidencias
              </button>
            ) : (
              pre?.puede_generar_evidencia && (
                <button
                  type="button"
                  onClick={() => void generarEvidencia()}
                  disabled={generando || creando}
                  className="btn-ghost text-xs disabled:opacity-50"
                >
                  {generando ? "Generando…" : "📦 Generar paquete de evidencias"}
                </button>
              )
            )}
          </div>

          {pre?.ia_habilitada && (
            <CorreosParaIA
              casoId={casoId}
              seleccion={correosIA}
              onCambio={setCorreosIA}
              className="border-t border-ink-700 pt-2"
            />
          )}

          {/* Apuntes de la bitácora, a la mano: si no están aquí hay que cerrar
              el modal e irse a otra pestaña a ver de qué se trata el caso. */}
          {(pre?.bitacora.length ?? 0) > 0 && (
            <div className="border-t border-ink-700 pt-2">
              <button
                type="button"
                onClick={() => setVerApuntes((v) => !v)}
                className="text-xs text-zinc-400 hover:text-zinc-200 transition"
              >
                {verApuntes ? "▾" : "▸"} Apuntes del caso ({pre?.bitacora.length})
              </button>
              {verApuntes && (
                <ul className="mt-2 space-y-2 max-h-52 overflow-y-auto">
                  {pre?.bitacora.map((n, i) => (
                    <li
                      key={i}
                      className="rounded border border-ink-700 bg-ink-800/40 px-2 py-1.5"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[10px] text-zinc-500">
                          {n.fecha}
                          {n.autor ? ` · ${n.autor}` : ""}
                        </span>
                        <button
                          type="button"
                          onClick={() => agregarAlFinal(n.texto)}
                          disabled={creando}
                          className="text-[10px] text-accent-400 hover:text-accent-300 disabled:opacity-50"
                        >
                          + agregar
                        </button>
                      </div>
                      <p className="text-xs text-zinc-300 whitespace-pre-wrap">
                        {n.texto}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {confirmando && (
            <p className="text-xs text-amber-300">
              Se va a abrir el ticket tal cual está arriba. ¿Le damos?
            </p>
          )}
        </div>
      )}
    </Modal>
  );
}
